// Project 3: AIService with backend API integration
import axios from 'axios';
import type {
  BlogGenerationRequest,
  BlogGenerationResponse,
  YouTubeScriptRequest,
  YouTubeScriptResponse,
} from '@/types/ai';

const apiClient = axios.create({
  baseURL: '/api',
  timeout: 180000, // 180초 (3분) - YouTube 스크립트는 여러 섹션 생성으로 시간 소요
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  // HTML 응답을 받지 않도록 설정
  validateStatus: function (status) {
    return status >= 200 && status < 300;
  },
  transformResponse: [
    function (data) {
      // HTML 응답인 경우 감지
      if (typeof data === 'string' && data.trim().startsWith('<!DOCTYPE')) {
        console.error('[apiClient] HTML 응답 감지됨 - JSON 파싱 실패');
        throw new Error('HTML_RESPONSE_DETECTED');
      }
      try {
        return JSON.parse(data);
      } catch (e) {
        console.error('[apiClient] JSON 파싱 실패:', e);
        throw new Error('JSON_PARSE_ERROR');
      }
    }
  ],
});

export class AIService {
  /**
   * 블로그 콘텐츠 생성
   * Backend endpoint: POST /api/v2/content/blog/generate
   */
  async generateBlogContent(request: BlogGenerationRequest): Promise<BlogGenerationResponse> {
    try {
      // 키워드 검증
      if (!request.keyword || request.keyword.trim().length === 0) {
        throw new Error('키워드를 입력해주세요.');
      }

      if (request.keyword.length > 200) {
        throw new Error('키워드가 너무 깁니다 (최대 200자).');
      }

      const response = await apiClient.post<BlogGenerationResponse>(
        '/v2/content/blog/generate',
        {
          keyword: request.keyword.trim(),
          userId: request.userId,
          personalityId: request.personalityId,
          contentType: request.contentType || 'informational',
          targetLength: request.targetLength || 'medium',
          seoOptimization: request.seoOptimization ?? true,
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || '블로그 생성에 실패했습니다.');
      }

      return response.data;
    } catch (error: any) {
      // 네트워크 오류 시 폴백 메시지
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('요청 시간이 초과되었습니다. 다시 시도해주세요.');
      }
      
      if (error.response?.status === 429) {
        throw new Error('요청 한도를 초과했습니다. 잠시 후 다시 시도해주세요.');
      }

      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      
      throw new Error(error.message || '블로그 생성 중 오류가 발생했습니다.');
    }
  }

  /**
   * 유튜브 스크립트 생성
   * Backend endpoint: POST /api/v2/content/youtube/generate
   */
  async generateYouTubeScript(request: YouTubeScriptRequest): Promise<YouTubeScriptResponse> {
    try {
      // 키워드 검증
      if (!request.keyword || request.keyword.trim().length === 0) {
        throw new Error('키워드를 입력해주세요.');
      }

      if (request.keyword.length > 200) {
        throw new Error('키워드가 너무 깁니다 (최대 200자).');
      }

      const requestBody = {
        keyword: request.keyword.trim(),
        title: request.title,
        subject: request.subject,
        userId: request.userId,
        personalityId: request.personalityId,
        videoType: request.videoType || 'tutorial',
        targetDuration: request.targetDuration || 'medium',
        includeTimestamps: request.includeTimestamps ?? true,
        // 사이드바 옵션 추가
        purpose: request.purpose,
        intent: request.intent,
        enableFactCheck: request.enableFactCheck ?? false,
        // 인격체 정보
        persona: request.persona,
      };
      
      console.log('[AIService] YouTube 스크립트 생성 요청:', requestBody);
      
      const response = await apiClient.post<YouTubeScriptResponse>(
        '/v2/content/youtube/generate',
        requestBody
      );

      console.log('[AIService] YouTube 스크립트 생성 응답:', response.data);

      // HTML 응답 체크 (세션 만료 또는 인증 실패 시 로그인 페이지가 반환될 수 있음)
      const responseData = response.data as any;
      const responseStr = typeof responseData === 'string' ? responseData : JSON.stringify(responseData);
      
      if (typeof responseData === 'string' && (responseData.trim().startsWith('<!DOCTYPE') || responseData.includes('<html'))) {
        console.error('[AIService] HTML 응답 수신 - 세션 만료 또는 인증 실패 가능성');
        throw new Error('로그인 세션이 만료되었습니다. 페이지를 새로고침하거나 다시 로그인해주세요.');
      }

      // JSON 파싱 실패 체크
      if (!responseData || typeof responseData !== 'object') {
        console.error('[AIService] 잘못된 응답 형식:', typeof responseData);
        throw new Error('서버로부터 올바른 응답을 받지 못했습니다. 페이지를 새로고침해주세요.');
      }

      // responseData가 객체지만 HTML 내용을 포함하는 경우 (문자열로 변환 시 DOCTYPE이 포함된 경우)
      try {
        const strCheck = JSON.stringify(responseData);
        if (strCheck.includes('<!DOCTYPE') || strCheck.includes('<html')) {
          console.error('[AIService] HTML 응답이 객체에 포함됨 - 세션 만료 가능성');
          throw new Error('로그인 세션이 만료되었습니다. 페이지를 새로고침하거나 다시 로그인해주세요.');
        }
      } catch (e) {
        // JSON 직렬화 실패는 무시
      }

      if (!responseData.success) {
        const errorMsg = responseData.error || '유튜브 스크립트 생성에 실패했습니다.';
        console.error('[AIService] YouTube 스크립트 생성 실패:', errorMsg);
        throw new Error(errorMsg);
      }

      return responseData;
    } catch (error: any) {
      console.error('[AIService] YouTube 스크립트 생성 예외 발생:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        code: error.code
      });
      
      // 네트워크 오류 시 폴백 메시지
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('요청 시간이 초과되었습니다. 다시 시도해주세요.');
      }
      
      // HTML 응답 감지 또는 JSON 파싱 오류
      if (error.message === 'HTML_RESPONSE_DETECTED' || error.message === 'JSON_PARSE_ERROR') {
        throw new Error('로그인 세션이 만료되었거나 서버 응답에 문제가 있습니다. 페이지를 새로고침하거나 다시 로그인해주세요.');
      }

      // 응답 데이터가 HTML인 경우 감지
      if (error.response?.data) {
        const responseStr = typeof error.response.data === 'string' 
          ? error.response.data 
          : JSON.stringify(error.response.data);
        if (responseStr.includes('<!DOCTYPE') || responseStr.includes('<html') || responseStr.includes('<title>')) {
          console.error('[AIService] HTML 응답 감지됨:', responseStr.substring(0, 200));
          throw new Error('로그인 세션이 만료되었습니다. 페이지를 새로고침하거나 다시 로그인해주세요.');
        }
      }
      
      // HTTP 상태 코드별 에러 처리
      if (error.response?.status === 401) {
        throw new Error('로그인이 필요합니다. 페이지를 새로고침해주세요.');
      }
      
      if (error.response?.status === 403) {
        throw new Error('접근 권한이 없습니다.');
      }
      
      if (error.response?.status === 429) {
        throw new Error('요청 한도를 초과했습니다. 잠시 후 다시 시도해주세요.');
      }
      
      if (error.response?.status === 500) {
        throw new Error('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      }

      // 백엔드에서 전달된 에러 메시지 사용
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      
      // 기본 에러 메시지
      throw new Error(error.message || '유튜브 스크립트 생성 중 오류가 발생했습니다.');
    }
  }
}

export const aiService = new AIService();


